/**
 * "Start offboarding" — `POST offboarding/requests/initiate/`. This is the
 * real workflow entry point (auto-runs reconciliation server-side in the same
 * transaction), not the plain CRUD create; manager roles only
 * (`assertManagerRole`). The lease is looked up through the caller's own
 * scope server-side, so a foreign lease id 404s rather than 403s — that
 * backend message is surfaced as-is via parseApiError.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { initiateOffboarding } from '../../api/endpoints/offboarding';

export function OffboardingInitiatePage(): React.JSX.Element {
  const navigate = useNavigate();
  const [leaseId, setLeaseId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const initiate = useMutation({
    mutationFn: () => initiateOffboarding({ lease_id: Number(leaseId) }),
    onSuccess: (request) => navigate(`../${request.id}`, { replace: true }),
    onError: (caught) => setError(parseApiError(caught).message),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    initiate.mutate();
  }

  return (
    <section>
      <h2>Start offboarding</h2>

      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="offboarding-lease-id">Lease id</label>
        <input
          id="offboarding-lease-id"
          type="number"
          required
          value={leaseId}
          onChange={(event) => setLeaseId(event.target.value)}
        />

        <button type="submit" disabled={initiate.isPending}>
          {initiate.isPending ? 'Starting…' : 'Start offboarding'}
        </button>
      </form>
    </section>
  );
}

export default OffboardingInitiatePage;
